"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

import {
  createContext,
  useContext,
  useEffect,
  useId,
  useInsertionEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { Slot } from "@radix-ui/react-slot";
import { AnimatePresence, motion } from "motion/react";
import { CircleAlert } from "lucide-react";
import { Button, type ButtonProps } from "@/components/wingo/ui/button";
import { useMotionPreset } from "@/components/wingo/lib/motion";
import { useDefaults } from "@/components/wingo/lib/ui-config";
import { cn } from "@/components/wingo/lib/utils";
import { withRef } from "@/components/wingo/lib/with-ref";

export type FieldSize = "sm" | "md" | "lg";
export type FieldVariant = "soft" | "outline" | "underline" | "ghost";
export type FieldRadius = "none" | "sm" | "md" | "lg" | "full";
export type FieldOrientation = "vertical" | "horizontal";
export type FieldLabelBehavior = "native" | "focus";
export type FieldSlot =
  | "labelRow"
  | "label"
  | "required"
  | "optional"
  | "labelAction"
  | "message"
  | "description"
  | "error"
  | "counter";

// ---------- the box: one recipe for every text field and trigger (DESIGN 9, W2 1.3) ----------

export const FIELD_BOX =
  "group/field relative flex w-full min-w-0 items-center text-foreground transition-[background-color,border-color,box-shadow] duration-fast ease-smooth focus-within:ring-2 focus-within:ring-ring/50 data-[invalid]:ring-2 data-[invalid]:ring-destructive/50 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50";

export const FIELD_VARIANTS: Record<FieldVariant, string> = {
  soft: "bg-foreground/5 hover:bg-foreground/7 data-[readonly]:hover:bg-foreground/5",
  // deviation from the spec string: readonly and invalid are excluded inside the hover selector; a separate data-[readonly]:hover
  // rule had the same specificity and lost, and the hover grey also beat the invalid red
  outline:
    "border border-input bg-transparent [&:not([data-readonly],[data-invalid]):hover:not(:focus-within)]:border-foreground/25 focus-within:border-ring data-[invalid]:border-destructive",
  // deviation: a new value. a bottom border that grows a 2px bar from the center on focus; no ring, no radius, no side padding
  underline:
    "rounded-none border-b border-input bg-transparent px-0 focus-within:ring-0 data-[invalid]:ring-0 data-[invalid]:border-destructive after:pointer-events-none after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:origin-center after:scale-x-0 after:bg-ring after:transition-transform after:duration-normal after:ease-smooth focus-within:after:scale-x-100 data-[invalid]:after:scale-x-100 data-[invalid]:after:bg-destructive",
  // inline edit, table cells, toolbars
  ghost: "bg-transparent hover:bg-foreground/5 data-[readonly]:hover:bg-transparent",
};

// single-line boxes; svgs without a size- class follow the size (14 / 16 / 18px)
export const FIELD_SIZES: Record<FieldSize, string> = {
  sm: "h-8 gap-1.5 px-2.5 text-base md:text-[13px] [&_svg:not([class*='size-'])]:size-3.5",
  md: "h-10 gap-2 px-3 text-base md:text-sm pointer-coarse:h-11 [&_svg:not([class*='size-'])]:size-4",
  lg: "h-12 gap-2 px-3.5 text-base md:text-[15px] [&_svg:not([class*='size-'])]:size-[18px]",
};

// boxes that grow: textarea, tag input and multi-select with wrap. chips (badge sm / md / lg = 20 / 24 / 28px) sit centered
export const FIELD_MULTILINE_SIZES: Record<FieldSize, string> = {
  sm: "h-auto min-h-8 flex-wrap items-start gap-1.5 px-2.5 py-1.5 text-base md:text-[13px] [&_svg:not([class*='size-'])]:size-3.5",
  md: "h-auto min-h-10 flex-wrap items-start gap-1.5 px-3 py-2 text-base md:text-sm pointer-coarse:min-h-11 pointer-coarse:py-2.5 [&_svg:not([class*='size-'])]:size-4",
  lg: "h-auto min-h-12 flex-wrap items-start gap-2 px-3.5 py-2.5 text-base md:text-[15px] [&_svg:not([class*='size-'])]:size-[18px]",
};

// the button's RADII, verbatim: the radius grows with the size so every size reads as the same shape
export const FIELD_RADII: Record<FieldRadius, Record<FieldSize, string>> = {
  none: { sm: "rounded-none", md: "rounded-none", lg: "rounded-none" },
  sm: { sm: "rounded-[6px]", md: "rounded-[8px]", lg: "rounded-[10px]" },
  md: { sm: "rounded-[8px]", md: "rounded-[10px]", lg: "rounded-[12px]" },
  lg: { sm: "rounded-[10px]", md: "rounded-[14px]", lg: "rounded-[16px]" },
  full: { sm: "rounded-full", md: "rounded-full", lg: "rounded-full" },
};

// the native element inside the box
export const FIELD_INPUT =
  "h-full min-w-0 flex-1 bg-transparent outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed read-only:cursor-default autofill:[transition:background-color_9999s]";

// prefix / suffix text and leading / trailing icons
export const FIELD_ADDON = "shrink-0 select-none text-muted-foreground";

// a box that is a <button> (select, date / time / color pickers): add to fieldBoxClasses(...)
export const FIELD_TRIGGER =
  "cursor-pointer touch-manipulation select-none text-start outline-hidden [-webkit-tap-highlight-color:transparent] data-[placeholder]:text-muted-foreground";

// the trigger's chevron; turns while the picker is open (the box carries data-state="open")
export const FIELD_CHEVRON =
  "size-4 shrink-0 text-muted-foreground transition-transform duration-fast ease-smooth group-data-[state=open]/field:rotate-180";

// the box's height as a css var, for horizontal field labels and pickers that align to it
export const FIELD_HEIGHT_VAR: Record<FieldSize, string> = {
  sm: "[--field-height:32px]",
  md: "[--field-height:40px] pointer-coarse:[--field-height:44px]",
  lg: "[--field-height:48px]",
};

const has = <T extends object>(map: T, key: unknown): key is keyof T =>
  typeof key === "string" && Object.prototype.hasOwnProperty.call(map, key);

export type FieldBoxClassOptions = {
  variant?: FieldVariant;
  size?: FieldSize;
  radius?: FieldRadius;
  multiline?: boolean;
  className?: string;
};

// the box classes for any element. unknown values fall back to soft / md / md; cn is tailwind-merge, so the
// underline's ring-0 and px-0 win over the base and the size
export function fieldBoxClasses({ variant, size, radius, multiline = false, className }: FieldBoxClassOptions = {}): string {
  const v: FieldVariant = has(FIELD_VARIANTS, variant) ? variant : "soft";
  const s: FieldSize = has(FIELD_SIZES, size) ? size : "md";
  const r: FieldRadius = has(FIELD_RADII, radius) ? radius : "md";
  return cn(
    FIELD_BOX,
    FIELD_VARIANTS[v],
    multiline ? FIELD_MULTILINE_SIZES[s] : FIELD_SIZES[s],
    v === "underline" ? "rounded-none px-0" : FIELD_RADII[r][s],
    className,
  );
}

// ---------- context ----------

export type FieldContextValue = {
  id: string;
  labelId: string;
  descriptionId: string;
  errorId: string;
  // the message ids a control should put in aria-describedby right now
  describedBy: string | undefined;
  labelledBy: string | undefined;
  hasLabel: boolean;
  invalid: boolean;
  disabled: boolean;
  required: boolean;
  readOnly: boolean;
  size: FieldSize;
  orientation: FieldOrientation;
  labelAs: "label" | "span";
  labelBehavior: FieldLabelBehavior;
};

type FieldGroupContextValue = {
  disabled: boolean;
  orientation?: FieldOrientation;
  labelWidth?: number;
};

const FieldContext = createContext<FieldContextValue | null>(null);
const FieldGroupContext = createContext<FieldGroupContextValue | null>(null);

// the nearest Field's ids and flags, or null outside one (for custom controls)
export function useFieldContext() {
  return useContext(FieldContext);
}

// "", false, null and undefined are no message; true only marks the field invalid
const isMessage = (node: ReactNode) => node !== undefined && node !== null && node !== false && node !== true && node !== "";

const idsFor = (id: string) => ({ labelId: `${id}-label`, descriptionId: `${id}-description`, errorId: `${id}-error` });

function describedByFor(id: string, description: ReactNode, error: ReactNode) {
  const showError = isMessage(error);
  const showDescription = !showError && isMessage(description);
  const { descriptionId, errorId } = idsFor(id);
  return [showDescription && descriptionId, showError && errorId].filter(Boolean).join(" ") || undefined;
}

const LABEL_SIZES: Record<FieldSize, string> = {
  sm: "text-[13px]/5",
  md: "text-sm/5",
  lg: "text-[15px]/6",
};

const FOCUSABLE =
  "input:not([type='hidden']):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex='-1'])";

// a span label (groups) and labelBehavior="focus" focus the control instead of clicking it. a group root is not focusable
// itself, so its roving tab stop (tabindex=0) or its first control takes focus
function focusControl(id: string | undefined) {
  if (!id) return;
  const element = document.getElementById(id);
  if (!element) return;
  const target = element.matches(FOCUSABLE)
    ? element
    : (element.querySelector<HTMLElement>("[tabindex='0']") ?? element.querySelector<HTMLElement>(FOCUSABLE));
  target?.focus();
}

// ---------- label ----------

export type FieldLabelProps = Omit<ComponentProps<"label">, "children"> & {
  // "span" for group controls, which point at it with aria-labelledby
  as?: "label" | "span";
  // "focus": a click focuses the control instead of clicking it, so a label never opens a picker
  behavior?: FieldLabelBehavior;
  size?: FieldSize;
  required?: boolean;
  optional?: boolean;
  optionalLabel?: ReactNode;
  requiredIndicator?: ReactNode;
  disabled?: boolean;
  classNames?: Partial<Record<"required" | "optional", string>>;
  children?: ReactNode;
};

// reads the nearest Field for its target, size and flags; every prop overrides
function FieldLabelRender({
  as,
  behavior,
  size,
  required,
  optional = false,
  optionalLabel = "Optional",
  requiredIndicator = "*",
  disabled,
  htmlFor,
  id,
  classNames,
  className,
  onClick,
  onMouseDown,
  children,
  ...props
}: FieldLabelProps) {
  const field = useContext(FieldContext);
  const tag = (as ?? field?.labelAs) === "span" ? "span" : "label";
  const mode = behavior ?? field?.labelBehavior ?? "native";
  const target = htmlFor ?? field?.id;
  const isRequired = required ?? field?.required ?? false;
  const isDisabled = disabled ?? field?.disabled ?? false;
  const scale = size ?? field?.size ?? "md";

  const handleClick = (event: MouseEvent<HTMLLabelElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || isDisabled) return;
    if (tag === "span" || mode === "focus") {
      event.preventDefault();
      focusControl(target);
    }
  };
  // a double click would select the label's text instead of doing nothing (radix label does the same)
  const handleMouseDown = (event: MouseEvent<HTMLLabelElement>) => {
    onMouseDown?.(event);
    if (!event.defaultPrevented && event.detail > 1) event.preventDefault();
  };

  const shared = {
    ...props,
    "data-slot": "field-label",
    "data-behavior": tag === "span" ? "focus" : mode,
    "data-disabled": isDisabled || undefined,
    id: id ?? field?.labelId,
    className: cn(
      "font-medium text-foreground transition-colors duration-fast ease-smooth",
      LABEL_SIZES[scale] ?? LABEL_SIZES.md,
      isDisabled && "text-foreground/45",
      className,
    ),
    onClick: handleClick,
    onMouseDown: handleMouseDown,
  };
  const content = (
    <>
      {children}
      {isRequired ? (
        <span data-slot="field-required" aria-hidden className={cn("ms-0.5 text-destructive-soft-foreground", classNames?.required)}>
          {requiredIndicator}
        </span>
      ) : optional ? (
        <span data-slot="field-optional" className={cn("ms-1.5 text-xs font-normal text-muted-foreground", classNames?.optional)}>
          {optionalLabel}
        </span>
      ) : null}
    </>
  );

  if (tag === "span") return <span {...(shared as unknown as ComponentProps<"span">)}>{content}</span>;
  return (
    <label {...shared} htmlFor={target}>
      {content}
    </label>
  );
}

// ---------- messages ----------

export type FieldDescriptionProps = ComponentProps<"p">;

function FieldDescriptionRender({ id, className, ...props }: FieldDescriptionProps) {
  const field = useContext(FieldContext);
  return (
    <p
      data-slot="field-description"
      id={id ?? field?.descriptionId}
      className={cn("min-w-0 flex-1 text-xs text-muted-foreground", field?.disabled && "text-foreground/45", className)}
      {...props}
    />
  );
}

export type FieldErrorProps = ComponentProps<"p"> & {
  // replaces the alert icon; null hides it
  icon?: ReactNode;
};

function FieldErrorRender({ id, icon, className, children, ...props }: FieldErrorProps) {
  const field = useContext(FieldContext);
  return (
    <p
      data-slot="field-error"
      id={id ?? field?.errorId}
      className={cn("flex min-w-0 flex-1 items-start gap-1.5 text-xs text-destructive-soft-foreground", className)}
      {...props}
    >
      {icon === undefined ? <CircleAlert aria-hidden className="size-3.5 shrink-0 translate-y-px" /> : icon}
      <span className="min-w-0">{children}</span>
    </p>
  );
}

export type FieldCounterProps = Omit<ComponentProps<"span">, "children"> & {
  count?: number;
  max: number;
};

// ok below 90%, near from 90%, over at or past the limit
function counterState(count: number, max: number): "ok" | "near" | "over" {
  if (max <= 0) return "ok";
  if (count >= max) return "over";
  return count >= max * 0.9 ? "near" : "ok";
}

// the visible "12 / 70"; screen readers get the announcement from FieldMessage instead
function FieldCounterRender({ count = 0, max, className, ...props }: FieldCounterProps) {
  return (
    <span
      data-slot="field-counter"
      data-state={counterState(count, max)}
      aria-hidden
      className={cn(
        "ms-auto shrink-0 text-xs tabular-nums text-muted-foreground transition-colors duration-fast ease-smooth data-[state=near]:text-warning-soft-foreground data-[state=over]:text-destructive-soft-foreground",
        className,
      )}
      {...props}
    >
      {count} / {max}
    </span>
  );
}

// speaks the remaining characters once 10% or less is left, 500ms after typing stops
function CounterStatus({ count, max, label }: { count: number; max: number; label: (remaining: number) => string }) {
  const [text, setText] = useState("");
  const latest = useRef(label);
  useInsertionEffect(() => {
    latest.current = label;
  });
  const remaining = max - count;
  const near = max > 0 && remaining <= max * 0.1;
  useEffect(() => {
    const timer = setTimeout(() => setText(near ? latest.current(remaining) : ""), near ? 500 : 0);
    return () => clearTimeout(timer);
  }, [near, remaining]);
  return (
    <span data-slot="field-counter-status" role="status" className="sr-only">
      {text}
    </span>
  );
}

const defaultCounterLabel = (remaining: number) => `${remaining} characters left`;

export type FieldMessageProps = Omit<ComponentProps<"div">, "children"> & {
  description?: ReactNode;
  error?: ReactNode;
  // current length and limit for the counter; maxLength 0 hides it
  count?: number;
  maxLength?: number;
  counterLabel?: (remaining: number) => string;
  descriptionId?: string;
  errorId?: string;
  classNames?: Partial<Record<"message" | "description" | "error" | "counter", string>>;
  "data-slot"?: string;
};

// the row under a control: the description or the error (never both) and the counter. it opens and closes with the
// collapse preset, description and error crossfade, nothing animates on the first render. the outer element stays
// mounted as the polite live region, so a new error is announced once
function FieldMessageRender({
  description,
  error,
  count,
  maxLength = 0,
  counterLabel,
  descriptionId,
  errorId,
  classNames,
  className,
  "data-slot": slot = "field-message",
  ...props
}: FieldMessageProps) {
  const field = useContext(FieldContext);
  const collapse = useMotionPreset("collapse");
  const fade = useMotionPreset("fade");
  const showError = isMessage(error);
  const showDescription = !showError && isMessage(description);
  const showCounter = maxLength > 0;
  const open = showError || showDescription || showCounter;

  return (
    <>
      <div data-slot={slot} aria-live="polite" className={cn("min-w-0", className)} {...props}>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div key="message" {...collapse} className="overflow-hidden">
              {/* the gap lives inside the collapsing wrapper, so opening and closing never jumps */}
              <div data-slot="field-message-row" className={cn("relative flex items-start gap-3 pt-1.5", classNames?.message)}>
                <AnimatePresence initial={false} mode="popLayout">
                  {showError ? (
                    <motion.div key="error" {...fade} className="flex min-w-0 flex-1">
                      <FieldError id={errorId ?? field?.errorId} className={classNames?.error}>
                        {error}
                      </FieldError>
                    </motion.div>
                  ) : showDescription ? (
                    <motion.div key="description" {...fade} className="flex min-w-0 flex-1">
                      <FieldDescription id={descriptionId ?? field?.descriptionId} className={classNames?.description}>
                        {description}
                      </FieldDescription>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
                {showCounter && <FieldCounter count={count} max={maxLength} className={classNames?.counter} />}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {showCounter && <CounterStatus count={count ?? 0} max={maxLength} label={counterLabel ?? defaultCounterLabel} />}
    </>
  );
}

// ---------- field ----------

export type FieldProps = Omit<ComponentProps<"div">, "id" | "children"> & {
  label?: ReactNode;
  // helper text under the control
  description?: ReactNode;
  // replaces the description while set, animates in and marks the field invalid. true marks it invalid without text
  error?: ReactNode;
  // the mark after the label; controls inside read it from context
  required?: boolean;
  // shows optionalLabel after the label, for forms that mark the few optional fields instead
  optional?: boolean;
  optionalLabel?: string;
  requiredIndicator?: ReactNode;
  // a link or small button at the end of the label row ("Forgot password?")
  labelAction?: ReactNode;
  // keeps the label for screen readers only
  hideLabel?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  // label size and the height horizontal labels align to; controls pass their own size
  size?: FieldSize;
  // horizontal: the label takes a column on the start side once the field is 448px wide. inherits from a FieldGroup
  orientation?: FieldOrientation;
  // px width of the label column in horizontal fields; inherits from a FieldGroup
  labelWidth?: number;
  // current length for the counter
  count?: number;
  // shows "count / max" when above 0
  maxLength?: number;
  // what screen readers hear once 10% or less is left; negative when over the limit
  counterLabel?: (remaining: number) => string;
  // the control's id (not the root's); children read it from context
  id?: string;
  labelBehavior?: FieldLabelBehavior;
  // "span" for group controls (radio group, checkbox group, rating), which use aria-labelledby
  labelAs?: "label" | "span";
  children?: ReactNode;
  classNames?: Partial<Record<FieldSlot, string>>;
};

function FieldRender(props: FieldProps) {
  const {
    label,
    description,
    error,
    required = false,
    optional = false,
    optionalLabel = "Optional",
    requiredIndicator = "*",
    labelAction,
    hideLabel = false,
    invalid = false,
    disabled = false,
    readOnly = false,
    size = "md",
    orientation = "vertical",
    labelWidth = 160,
    count = 0,
    maxLength = 0,
    counterLabel = defaultCounterLabel,
    id: idProp,
    labelBehavior = "native",
    labelAs = "label",
    children,
    classNames,
    className,
    style,
    ...rest
  } = useDefaults("field", props);
  const group = useContext(FieldGroupContext);
  const generated = useId();
  const id = idProp ?? generated;
  const { labelId, descriptionId, errorId } = idsFor(id);

  const scale: FieldSize = has(LABEL_SIZES, size) ? size : "md";
  // an explicit prop wins, then the enclosing FieldGroup, then the default
  const layout: FieldOrientation = (props.orientation ?? group?.orientation ?? orientation) === "horizontal" ? "horizontal" : "vertical";
  const horizontal = layout === "horizontal";
  const widthValue = props.labelWidth ?? group?.labelWidth ?? labelWidth;
  const width = Number.isFinite(widthValue) && widthValue > 0 ? widthValue : 160;

  const hasLabel = isMessage(label);
  const isInvalid = invalid || isMessage(error) || error === true;
  const isDisabled = disabled || Boolean(group?.disabled);
  const describedBy = describedByFor(id, description, error);
  const tag = labelAs === "span" ? "span" : "label";
  const behavior = labelBehavior === "focus" ? "focus" : "native";

  const context = useMemo<FieldContextValue>(
    () => ({
      id,
      labelId,
      descriptionId,
      errorId,
      describedBy,
      labelledBy: hasLabel ? labelId : undefined,
      hasLabel,
      invalid: isInvalid,
      disabled: isDisabled,
      required,
      readOnly,
      size: scale,
      orientation: layout,
      labelAs: tag,
      labelBehavior: behavior,
    }),
    [id, labelId, descriptionId, errorId, describedBy, hasLabel, isInvalid, isDisabled, required, readOnly, scale, layout, tag, behavior],
  );

  const labelNode = hasLabel ? (
    <FieldLabel
      as={tag}
      behavior={behavior}
      size={scale}
      required={required}
      optional={optional}
      optionalLabel={optionalLabel}
      requiredIndicator={requiredIndicator}
      disabled={isDisabled}
      htmlFor={id}
      id={labelId}
      className={cn(hideLabel && "sr-only", classNames?.label)}
      classNames={{ required: classNames?.required, optional: classNames?.optional }}
    >
      {label}
    </FieldLabel>
  ) : null;

  const action = labelAction ? (
    <div
      data-slot="field-label-action"
      className={cn(
        // a 44px hit area on touch for the link or button inside
        "ms-auto flex shrink-0 items-center text-[13px] *:relative pointer-coarse:*:after:absolute pointer-coarse:*:after:-inset-2",
        classNames?.labelAction,
      )}
    >
      {labelAction}
    </div>
  ) : null;

  // a hidden label alone renders sr-only straight into the root, so it takes no room
  const labelRow =
    action || (labelNode && !hideLabel) ? (
      <div
        data-slot="field-label-row"
        className={cn(
          "flex min-h-5 min-w-0 items-center justify-between gap-2",
          // self-start: the label lines up with the box, not with the box plus the message under it
          horizontal && "@md/field:min-h-[var(--field-height)] @md/field:items-center @md/field:self-start",
          classNames?.labelRow,
        )}
      >
        {labelNode}
        {action}
      </div>
    ) : (
      labelNode
    );

  const message = (
    <FieldMessage
      description={description}
      error={error}
      count={count}
      maxLength={maxLength}
      counterLabel={counterLabel}
      descriptionId={descriptionId}
      errorId={errorId}
      // cancels the root's gap while the row is closed; the row brings its own gap inside the collapse
      className="-mt-1.5"
      classNames={{
        message: classNames?.message,
        description: classNames?.description,
        error: classNames?.error,
        counter: classNames?.counter,
      }}
    />
  );

  return (
    <FieldContext.Provider value={context}>
      <div
        data-slot="field"
        data-orientation={layout}
        data-size={scale}
        data-invalid={isInvalid || undefined}
        data-disabled={isDisabled || undefined}
        data-readonly={readOnly || undefined}
        className={cn("flex w-full min-w-0 flex-col gap-1.5", FIELD_HEIGHT_VAR[scale], horizontal && "@container/field", className)}
        style={horizontal ? ({ "--field-label-width": `${width}px`, ...style } as CSSProperties) : style}
        {...rest}
      >
        {horizontal ? (
          // the field follows its own width, not the viewport: the label moves beside the control from 448px
          <div
            data-slot="field-layout"
            className="grid w-full min-w-0 gap-x-4 gap-y-1.5 @md/field:grid-cols-[var(--field-label-width)_minmax(0,1fr)]"
          >
            {labelRow}
            <div data-slot="field-content" className="flex min-w-0 flex-col gap-1.5 @md/field:col-start-2">
              {children}
              {message}
            </div>
          </div>
        ) : (
          <>
            {labelRow}
            {children}
            {message}
          </>
        )}
      </div>
    </FieldContext.Provider>
  );
}

// ---------- control wiring ----------

export type FieldControlSlot = "field" | "label" | "description" | "error" | "counter";

export type FieldControlOptions = {
  id?: string;
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  size?: FieldSize;
  count?: number;
  maxLength?: number;
  labelBehavior?: FieldLabelBehavior;
  labelAs?: "label" | "span";
  // anything else for the Field wrapper: optional, labelAction, orientation, labelWidth, hideLabel, className
  fieldProps?: Partial<FieldProps>;
  // the field parts of the control's classNames; other keys are ignored
  classNames?: Partial<Record<FieldControlSlot, string>>;
  className?: string;
  style?: CSSProperties;
};

export type FieldControlState = {
  id: string;
  labelId: string;
  descriptionId: string;
  errorId: string;
  describedBy: string | undefined;
  labelledBy: string | undefined;
  invalid: boolean;
  disabled: boolean;
  required: boolean;
  readOnly: boolean;
  size: FieldSize;
  orientation: FieldOrientation;
  // true when wrap() renders a Field around the control: className and style then belong to the Field root
  isWrapped: boolean;
  // for a native input, textarea or select
  controlProps: {
    id: string;
    "aria-describedby": string | undefined;
    "aria-invalid": true | undefined;
    required: true | undefined;
    disabled: true | undefined;
    readOnly: true | undefined;
  };
  // for anything else (a trigger div, a radix root)
  ariaProps: {
    id: string;
    "aria-describedby": string | undefined;
    "aria-invalid": true | undefined;
    "aria-required": true | undefined;
    "aria-disabled": true | undefined;
    "aria-readonly": true | undefined;
    "aria-labelledby": string | undefined;
  };
  wrap: (control: ReactNode) => ReactNode;
};

// the one hook every form control calls: merges its props with a parent Field or FieldGroup and returns the ids and flags
// for the focusable element, plus wrap(), which renders the Field anatomy around the control when it has its own label,
// description, error or counter. inside a parent Field it reuses the parent's ids and never wraps
export function useFieldControl(options: FieldControlOptions): FieldControlState {
  const {
    id: idProp,
    label,
    description,
    error,
    required = false,
    invalid = false,
    disabled = false,
    readOnly = false,
    size: sizeOption,
    count,
    maxLength = 0,
    labelBehavior,
    labelAs,
    fieldProps,
    classNames,
    className,
    style,
  } = options;
  const parent = useContext(FieldContext);
  const group = useContext(FieldGroupContext);
  const generated = useId();
  // the control's own size, else the enclosing Field's, else md (unknown values from a cms fall back too)
  const size: FieldSize = has(FIELD_SIZES, sizeOption) ? sizeOption : (parent?.size ?? "md");

  // the control's own props win; fieldProps fills in the rest, so ids, flags and the rendered Field always agree
  const extra = fieldProps ?? {};
  const labelNode = isMessage(label) ? label : extra.label;
  const descriptionNode = description ?? extra.description;
  const errorNode = error ?? extra.error;
  const limit = maxLength || extra.maxLength || 0;
  const own = {
    required: required || Boolean(extra.required),
    invalid: invalid || Boolean(extra.invalid),
    disabled: disabled || Boolean(extra.disabled),
    readOnly: readOnly || Boolean(extra.readOnly),
  };
  const hasLabel = isMessage(labelNode);

  const nestedLabel = Boolean(parent) && hasLabel;
  useEffect(() => {
    if (nestedLabel && process.env.NODE_ENV !== "production")
      console.warn("[field] a control inside <Field> got its own label; the Field's label wins. Remove the control's label prop.");
  }, [nestedLabel]);

  const isWrapped = !parent && (hasLabel || isMessage(descriptionNode) || isMessage(errorNode) || limit > 0);
  const id = parent?.id ?? idProp ?? extra.id ?? generated;
  const ids = parent ? { labelId: parent.labelId, descriptionId: parent.descriptionId, errorId: parent.errorId } : idsFor(id);
  const describedBy = parent ? parent.describedBy : isWrapped ? describedByFor(id, descriptionNode, errorNode) : undefined;
  const labelledBy = parent ? parent.labelledBy : isWrapped && hasLabel ? ids.labelId : undefined;
  const isInvalid = own.invalid || isMessage(errorNode) || errorNode === true || Boolean(parent?.invalid);
  const isDisabled = own.disabled || Boolean(parent?.disabled) || Boolean(group?.disabled);
  const isRequired = own.required || Boolean(parent?.required);
  const isReadOnly = own.readOnly || Boolean(parent?.readOnly);
  const orientation: FieldOrientation =
    (parent?.orientation ?? extra.orientation ?? group?.orientation) === "horizontal" ? "horizontal" : "vertical";
  const spanLabel = (parent?.labelAs ?? labelAs ?? extra.labelAs) === "span";

  const wrap = (control: ReactNode) =>
    isWrapped ? (
      <Field
        {...extra}
        id={id}
        label={labelNode}
        description={descriptionNode}
        error={errorNode}
        required={own.required}
        invalid={own.invalid}
        disabled={own.disabled}
        readOnly={own.readOnly}
        size={size}
        count={count ?? extra.count}
        maxLength={limit}
        labelBehavior={labelBehavior ?? extra.labelBehavior}
        labelAs={labelAs ?? extra.labelAs}
        className={cn(extra.className, classNames?.field, className)}
        style={extra.style || style ? { ...extra.style, ...style } : undefined}
        classNames={{
          ...extra.classNames,
          label: cn(extra.classNames?.label, classNames?.label) || undefined,
          description: cn(extra.classNames?.description, classNames?.description) || undefined,
          error: cn(extra.classNames?.error, classNames?.error) || undefined,
          counter: cn(extra.classNames?.counter, classNames?.counter) || undefined,
        }}
      >
        {control}
      </Field>
    ) : (
      control
    );

  return {
    id,
    ...ids,
    describedBy,
    labelledBy,
    invalid: isInvalid,
    disabled: isDisabled,
    required: isRequired,
    readOnly: isReadOnly,
    size,
    orientation,
    isWrapped,
    controlProps: {
      id,
      "aria-describedby": describedBy,
      "aria-invalid": isInvalid || undefined,
      required: isRequired || undefined,
      disabled: isDisabled || undefined,
      readOnly: isReadOnly || undefined,
    },
    ariaProps: {
      id,
      "aria-describedby": describedBy,
      "aria-invalid": isInvalid || undefined,
      "aria-required": isRequired || undefined,
      "aria-disabled": isDisabled || undefined,
      "aria-readonly": isReadOnly || undefined,
      "aria-labelledby": spanLabel ? labelledBy : undefined,
    },
    wrap,
  };
}

export type FieldControlProps = ComponentProps<typeof Slot>;

// wires the nearest Field's id and aria onto any single child: a native input or select, a range, a third-party control.
// the child's own props win
function FieldControlRender(props: FieldControlProps) {
  const field = useContext(FieldContext);
  // slot props are typed for any element; disabled and readOnly only mean something on form controls, which is what
  // goes here, so they are only passed when set
  const flags = { ...(field?.disabled && { disabled: true }), ...(field?.readOnly && { readOnly: true }) };
  return (
    <Slot
      data-slot="field-control"
      id={field?.id}
      aria-describedby={field?.describedBy}
      aria-invalid={field?.invalid || undefined}
      aria-required={field?.required || undefined}
      aria-labelledby={field?.labelAs === "span" ? field.labelledBy : undefined}
      {...flags}
      {...props}
    />
  );
}

// calls onReset after the element's form is reset. the reset event fires before the form puts its fields back, and a
// click-triggered reset even runs microtasks in between, so the callback waits one task
export function useFormReset(ref: RefObject<HTMLElement | null>, onReset: () => void) {
  const latest = useRef(onReset);
  useInsertionEffect(() => {
    latest.current = onReset;
  });
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const form = (element as Partial<HTMLInputElement>).form ?? element.closest("form");
    if (!form) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const handle = () => {
      clearTimeout(timer);
      timer = setTimeout(() => latest.current(), 0);
    };
    form.addEventListener("reset", handle);
    return () => {
      form.removeEventListener("reset", handle);
      clearTimeout(timer);
    };
  }, [ref]);
}

// ---------- group ----------

const GROUP_COLUMNS = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-4",
} as const;

const GROUP_GAPS = { sm: "gap-3", md: "gap-4", lg: "gap-6" } as const;

export type FieldGroupProps = Omit<ComponentProps<"fieldset">, "children"> & {
  // the fieldset's name, announced before its fields
  legend?: ReactNode;
  description?: ReactNode;
  // a message for the whole group, linked to the fieldset
  error?: ReactNode;
  // one column on phones, 2 from sm, 3 and 4 from md; span with className="sm:col-span-2"
  columns?: 1 | 2 | 3 | 4;
  // card: a raised surface with the legend inside
  variant?: "plain" | "card";
  // inherited by every Field and control inside
  orientation?: FieldOrientation;
  labelWidth?: number;
  gap?: "sm" | "md" | "lg";
  // the native fieldset attribute (disables every native control inside) plus context for ours
  disabled?: boolean;
  // a mark after the legend
  required?: boolean;
  requiredIndicator?: ReactNode;
  classNames?: Partial<Record<"legend" | "description" | "error" | "content", string>>;
  children?: ReactNode;
};

function FieldGroupRender(fieldGroupProps: FieldGroupProps) {
  const {
    legend,
    description,
    error,
    columns = 1,
    variant = "plain",
    orientation,
    labelWidth,
    gap = "md",
    disabled = false,
    required = false,
    requiredIndicator = "*",
    classNames,
    className,
    id: idProp,
    children,
    ...props
  } = useDefaults("field-group", fieldGroupProps);
  const parent = useContext(FieldGroupContext);
  const generated = useId();
  const id = idProp ?? generated;
  const isDisabled = disabled || Boolean(parent?.disabled);
  const layout = orientation ?? parent?.orientation;
  const width = labelWidth ?? parent?.labelWidth;
  const context = useMemo(() => ({ disabled: isDisabled, orientation: layout, labelWidth: width }), [isDisabled, layout, width]);
  const showDescription = isMessage(description);
  const hasHeader = isMessage(legend) || showDescription;
  const ids = idsFor(id);
  // unlike a field, the group keeps its description while an error shows, so both describe it
  const describedBy = [showDescription && ids.descriptionId, isMessage(error) && ids.errorId].filter(Boolean).join(" ") || undefined;

  return (
    <FieldGroupContext.Provider value={context}>
      <fieldset
        data-slot="field-group"
        data-variant={variant}
        data-orientation={layout ?? "vertical"}
        data-disabled={isDisabled || undefined}
        id={idProp}
        disabled={disabled || undefined}
        aria-describedby={describedBy}
        className={cn(
          "min-w-0",
          variant === "card" && "rounded-3xl bg-surface p-4 text-surface-foreground shadow-raised sm:p-5",
          className,
        )}
        {...props}
      >
        {isMessage(legend) && (
          // floated, so the legend sits inside the padding like any heading instead of on the fieldset's border
          <legend
            data-slot="field-group-legend"
            className={cn(
              "float-start w-full text-[15px] font-semibold tracking-tight text-foreground transition-colors duration-fast",
              isDisabled && "text-foreground/45",
              classNames?.legend,
            )}
          >
            {legend}
            {required && (
              <span data-slot="field-group-required" aria-hidden className="ms-0.5 text-destructive-soft-foreground">
                {requiredIndicator}
              </span>
            )}
          </legend>
        )}
        <div data-slot="field-group-body" className="clear-both flex min-w-0 flex-col">
          {showDescription && (
            <p
              data-slot="field-group-description"
              id={ids.descriptionId}
              className={cn("mt-1 text-sm text-muted-foreground", isDisabled && "text-foreground/45", classNames?.description)}
            >
              {description}
            </p>
          )}
          <FieldMessage
            data-slot="field-group-error"
            error={error}
            errorId={ids.errorId}
            // without a header the error sits right on the fields, so its gap moves below it (inside the collapse)
            classNames={{ error: classNames?.error, message: hasHeader ? undefined : "pt-0 pb-3" }}
          />
          <div
            data-slot="field-group-content"
            className={cn(
              "grid min-w-0",
              GROUP_COLUMNS[columns] ?? GROUP_COLUMNS[1],
              GROUP_GAPS[gap] ?? GROUP_GAPS.md,
              hasHeader && "mt-4",
              classNames?.content,
            )}
          >
            {children}
          </div>
        </div>
      </fieldset>
    </FieldGroupContext.Provider>
  );
}

// ---------- box parts ----------

export type FieldBoxProps = ComponentProps<"div"> & {
  variant?: FieldVariant;
  // defaults to the nearest Field's size, else md
  size?: FieldSize;
  radius?: FieldRadius;
  // grows with its content: textarea, chips
  multiline?: boolean;
  // default to the nearest Field's flags
  invalid?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  // for pickers: turns the chevron and marks the open trigger
  state?: "open" | "closed";
  // gives a trigger <button> (or a native select) the box classes and data attributes
  asChild?: boolean;
  // a press on the padding, an addon or an icon focuses the input inside
  focusOnPointerDown?: boolean;
  // "field-box" by default; items write their own ("input-box", "select-trigger")
  "data-slot"?: string;
};

const INTERACTIVE = "input, textarea, select, button, a[href], [contenteditable='true'], [role='button']";

function FieldBoxRender(fieldBoxProps: FieldBoxProps) {
  const {
    variant = "soft",
    size,
    radius = "md",
    multiline = false,
    invalid,
    disabled,
    readOnly,
    state,
    asChild = false,
    focusOnPointerDown = true,
    "data-slot": slot = "field-box",
    className,
    onPointerDown,
    onClick,
    ...props
  } = useDefaults("field-box", fieldBoxProps);
  const field = useContext(FieldContext);
  const group = useContext(FieldGroupContext);
  // unknown values (from a cms) fall back like fieldBoxClasses, so the data attributes match the look
  const look: FieldVariant = has(FIELD_VARIANTS, variant) ? variant : "soft";
  const scale: FieldSize = has(FIELD_SIZES, size) ? size : (field?.size ?? "md");
  const isInvalid = invalid ?? field?.invalid ?? false;
  const isDisabled = disabled ?? field?.disabled ?? group?.disabled ?? false;
  const isReadOnly = readOnly ?? field?.readOnly ?? false;

  // the input inside, when the press landed on the box itself, an addon or an icon (never on a button or the input)
  const inputFor = (event: MouseEvent<HTMLDivElement>) => {
    if (!focusOnPointerDown || isDisabled || event.defaultPrevented) return null;
    const box = event.currentTarget;
    const hit = (event.target as Element).closest(INTERACTIVE);
    if (hit && box.contains(hit)) return null;
    const input = box.querySelector<HTMLInputElement | HTMLTextAreaElement>("input:not([type='hidden']), textarea");
    return input && !input.disabled ? input : null;
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    onPointerDown?.(event);
    if (event.button !== 0) return;
    const input = inputFor(event);
    if (!input) return;
    // keeps focus and the caret where they are. a finger on an unfocused field focuses on click instead, the only
    // moment ios raises the keyboard
    if (document.activeElement === input || event.pointerType !== "touch") {
      event.preventDefault();
      input.focus();
    }
  };
  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    const input = inputFor(event);
    if (input && document.activeElement !== input) input.focus();
  };

  const Comp = asChild ? Slot : "div";
  return (
    <Comp
      data-slot={slot}
      data-field-box=""
      data-variant={look}
      data-size={scale}
      data-multiline={multiline || undefined}
      data-invalid={isInvalid || undefined}
      data-disabled={isDisabled || undefined}
      data-readonly={isReadOnly || undefined}
      data-state={state}
      className={fieldBoxClasses({ variant: look, size: scale, radius, multiline, className })}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      {...props}
    />
  );
}

export type FieldAddonProps = ComponentProps<"span">;

// text addons ("https://", "USD") in the muted addon look; elements (a button, a select) render as given, bare icons muted
function FieldAddonRender({ className, children, ...props }: FieldAddonProps) {
  const text = typeof children === "string" || typeof children === "number";
  return (
    <span
      data-slot="field-addon"
      className={cn(text ? FIELD_ADDON : "inline-flex shrink-0 items-center [&>svg]:text-muted-foreground", className)}
      {...props}
    >
      {children}
    </span>
  );
}

export type FieldActionProps = Omit<ButtonProps, "size" | "variant" | "children"> & {
  // the accessible name and the hover title
  label: string;
  icon?: ReactNode;
  // 28px in sm and md boxes, 32px in lg; defaults to the nearest Field's size, else md
  size?: FieldSize;
  // the input keeps focus while the action is pressed
  keepFocus?: boolean;
  children?: ReactNode;
};

// the trailing icon button of a box: clear, reveal, steppers, the calendar button. 44px hit area on touch
function FieldActionRender({
  label,
  icon,
  size,
  keepFocus = true,
  className,
  onPointerDown,
  onMouseDown,
  children,
  ...props
}: FieldActionProps) {
  const field = useContext(FieldContext);
  const scale = size ?? field?.size ?? "md";
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      radius="full"
      aria-label={label}
      title={label}
      data-slot="field-action"
      className={cn(
        "-me-1.5 size-7 text-muted-foreground hover:text-foreground pointer-coarse:after:-inset-2 group-data-[variant=underline]/field:me-0 [&_svg:not([class*='size-'])]:size-4",
        scale === "lg" && "size-8",
        className,
      )}
      onPointerDown={(event) => {
        onPointerDown?.(event);
        if (keepFocus) event.preventDefault();
      }}
      onMouseDown={(event) => {
        onMouseDown?.(event);
        if (keepFocus) event.preventDefault();
      }}
      {...props}
    >
      {icon ?? children}
    </Button>
  );
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const FieldLabel = withRef(FieldLabelRender, "FieldLabel");
export const FieldDescription = withRef(FieldDescriptionRender, "FieldDescription");
export const FieldError = withRef(FieldErrorRender, "FieldError");
export const FieldCounter = withRef(FieldCounterRender, "FieldCounter");
export const FieldMessage = withRef(FieldMessageRender, "FieldMessage");
export const Field = withRef(FieldRender, "Field");
export const FieldControl = withRef(FieldControlRender, "FieldControl");
export const FieldGroup = withRef(FieldGroupRender, "FieldGroup");
export const FieldBox = withRef(FieldBoxRender, "FieldBox");
export const FieldAddon = withRef(FieldAddonRender, "FieldAddon");
export const FieldAction = withRef(FieldActionRender, "FieldAction");
